import { type RefObject, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { differenceInMinutes } from "date-fns";

import { invalidateDealTodoSurfaces } from "@/hooks/use-agent-todos";
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";
import { sectionSelector } from "@/components/overview/overview-sections";
import { watchFor } from "@/components/ui/docked-tray";
import { findTarget, isTarget, parseTarget, stillTarget } from "@/lib/kory-pointing";
import { refSelector } from "@/lib/kory-refs";
// Read at call time only, so these imports' cycles (live-cards, the session hook) are safe.
import { COS_SESSION_QUERY_KEY } from "@/components/overview/cos-v2/use-cos-session";
import { PENDING_PREFIX } from "./live-cards";

/**
 * The cards Kory's latest reply names (``TodoDTO.id`` keys, the rows'
 * ``data-todo-id``), shared by every surface that shows them: the Kory chat
 * stream's ``referenced_todos`` event, a reloaded reply's ``referencedTodoIds``,
 * and the voice harness's results.
 *
 * The mark is sticky, with no timer: a card Kory names stays highlighted (and expanded, in view)
 * while you read it, through replies that name no cards, until one of: a later reply names different
 * cards; Kory's reply lets go of them (``releaseReferencedTodos``, the server's ``released``
 * reference: the conversation moved on); you act on the card (``unmarkTodos``); you fold the tray
 * or the call ends (``unmarkReferencedTodos``). Naming the same cards again never re-scrolls.
 * ``kept`` is the last cards any reply named: they stay in a docked tray's top rows after the mark
 * goes, until a later reply names others, so a card Kory just put there never drops behind See all.
 * ``released`` counts Kory letting go: a tray that opened itself for the cards folds back on it.
 * ``named`` counts replies that name cards, the same ones again included: during a call, Kory naming a
 * card that is already marked still shows it.
 * ``nouns`` are the other records the same reply names (``<type>:<id>``, the page nouns' types: a rock, a
 * KPI, a deal), marked wherever a surface shows them (``data-kory-ref``, ``KoryPointingLayer``) by the same rules.
 * ``sections`` are the Overview sections the same reply names (``data-overview-section`` keys), marked
 * and scrolled to on the page by the same rules; you act on one by touching it (``unmarkSections``).
 * They also hold what the reply names on any page by role and accessible name (``region "Cash"``,
 * ``kory-pointing``), marked by ``usePointedTargets`` by the same rules, and the page parts it names
 * (``overview:win-the-day``, ``kory-refs``), marked wherever a surface carries them.
 * ``order`` is everything the reply points at with a numbered chip, in Kory's numbering: its cards (``todo:<id>``),
 * then its records, then its targets, as ``referenceOrder`` writes it. Each keeps its number while
 * marked; acting on one never renumbers the rest (``kory-focus``'s ``pointNumber``).
 */

/** ``changed`` counts results that changed cards this tab already shows (a yes that acted, an edit). */
export type ReferencedTodos = {
  ids: readonly string[];
  nouns: readonly string[];
  sections: readonly string[];
  nonce: number;
  kept: readonly string[];
  changed: number;
  released: number;
  named: number;
  order: readonly string[];
};

const EMPTY: ReferencedTodos = {
  ids: [],
  nouns: [],
  sections: [],
  nonce: 0,
  kept: [],
  changed: 0,
  released: 0,
  named: 0,
  order: [],
};
let snapshot: ReferencedTodos = EMPTY;
let lastReply: string | null = null;
const listeners = new Set<() => void>();
const seenReplies = new Set<string>();
// The records each to-do's mounted cards show, counted per card (``useCardNouns``): acting on the to-do
// lets go of those too.
const cardNouns = new Map<string, Map<string, number>>();

function emit(next: ReferencedTodos) {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** A reply's ``nouns`` (``{type, id}``) as the store's ``<type>:<id>`` refs. */
export function nounRefs(nouns: readonly { type: string; id: string }[] | null | undefined): string[] {
  return (nouns ?? []).map((noun) => `${noun.type}:${noun.id}`);
}

/** Kory's numbering of what a reply points at with a chip: its cards, then its records, then its targets
 *  (an Overview section is not numbered). The backend numbers them the same way
 *  (``references.badges``), so "the second one" in Kory's words is the 2 on screen. */
export function referenceOrder(ids: readonly string[], nouns: readonly string[], sections: readonly string[]): string[] {
  return [...ids.map((id) => `todo:${id}`), ...nouns, ...sections.filter(isTarget)];
}

const sameIds = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((id, i) => b[i] === id);

/** Mark ``ids`` (in the reply's order) as the cards, ``sections`` as the Overview sections, and ``nouns``
 *  (``<type>:<id>``) as the other records Kory means in ``reply``, numbered by ``order`` when the reply
 *  carries its pointing's numbering (a card an edit added is marked, unnumbered), else by ``referenceOrder``. Naming nothing leaves the mark: a reply that names nothing is still about them
 *  until Kory lets go. A new reply naming other things scrolls once, and so does the same reply moving
 *  on to another part (reading the brief); the same again, or the same reply's list growing, only
 *  re-marks. A reply naming only sections keeps the cards in ``kept``. */
export function referTodos(
  ids: readonly string[],
  reply: string,
  sections: readonly string[] = [],
  nouns: readonly string[] = [],
  pointed?: readonly string[],
) {
  const fresh = reply !== lastReply;
  lastReply = reply;
  if (!ids.length && !sections.length && !nouns.length) return;
  const named = fresh ? snapshot.named + 1 : snapshot.named;
  const order = pointed ? [...pointed] : referenceOrder(ids, nouns, sections);
  if (sameIds(ids, snapshot.ids) && sameIds(sections, snapshot.sections) && sameIds(nouns, snapshot.nouns)) {
    // A new reply naming what is left (you acted on the others) numbers it afresh, as the backend does;
    // so does the same reply pointing at what an edit or a read-back already marked.
    if (fresh || !sameIds(order, snapshot.order)) emit({ ...snapshot, named, order });
    return;
  }
  const kept = ids.length ? [...ids] : snapshot.kept;
  // A skeleton handing its mark to the card it became is the same card: no second scroll.
  const handoff = snapshot.ids[0]?.startsWith(PENDING_PREFIX) ?? false;
  const moved =
    (ids[0] !== snapshot.ids[0] && !handoff) || sections[0] !== snapshot.sections[0] || nouns[0] !== snapshot.nouns[0];
  const nonce = fresh || moved ? snapshot.nonce + 1 : snapshot.nonce;
  emit({ ...snapshot, ids: [...ids], nouns: [...nouns], sections: [...sections], nonce, kept, named, order });
}

/** Kory's reply moved on to something else: the mark goes, and a tray that opened itself for the
 *  cards folds back, even when you already acted on them and nothing is marked. */
export function releaseReferencedTodos() {
  emit({ ...snapshot, ids: [], nouns: [], sections: [], order: [], released: snapshot.released + 1 });
}

/** You acted on these cards (approve, dismiss, edit, open): they stop being the ones Kory means, and so do
 *  the records the cards show. */
export function unmarkTodos(ids: readonly string[]) {
  const left = snapshot.ids.filter((id) => !ids.includes(id));
  const shown = new Set(ids.flatMap((id) => [...(cardNouns.get(id)?.keys() ?? [])]));
  const nouns = snapshot.nouns.filter((ref) => !shown.has(ref));
  if (left.length !== snapshot.ids.length || nouns.length !== snapshot.nouns.length) emit({ ...snapshot, ids: left, nouns });
}

/** A card for to-do ``todoId`` shows the records ``nouns`` (``<type>:<id>``): acting on it lets go of them. */
export function useCardNouns(todoId: string | null | undefined, nouns: readonly string[]) {
  const key = nouns.join(" ");
  useEffect(() => {
    if (!todoId || !key) return;
    const refs = key.split(" ");
    const known = cardNouns.get(todoId) ?? new Map<string, number>();
    for (const ref of refs) known.set(ref, (known.get(ref) ?? 0) + 1);
    cardNouns.set(todoId, known);
    return () => {
      for (const ref of refs) {
        const left = (known.get(ref) ?? 1) - 1;
        if (left) known.set(ref, left);
        else known.delete(ref);
      }
      if (!known.size && cardNouns.get(todoId) === known) cardNouns.delete(todoId);
    };
  }, [todoId, key]);
}

/** The cards marked right now, read outside React (an edit call starting on the card Kory means). */
export function markedTodos(): readonly string[] {
  return snapshot.ids;
}

/** You touched these Overview sections: they stop being the ones Kory means. */
export function unmarkSections(keys: readonly string[]) {
  const left = snapshot.sections.filter((key) => !keys.includes(key));
  if (left.length !== snapshot.sections.length) emit({ ...snapshot, sections: left });
}

/** Acting on the card Kory means (approve, dismiss, edit, open) is the end of its mark: `fn`, after
 *  unmarking the card `idOf` names from its first argument. */
export function actingOn<T, A extends unknown[]>(idOf: (card: T) => string, fn: (card: T, ...rest: A) => void) {
  return (card: T, ...rest: A) => {
    unmarkTodos([idOf(card)]);
    fn(card, ...rest);
  };
}

/** You folded the tray, or the call ended: nothing stays marked. */
export function unmarkReferencedTodos() {
  if (snapshot.ids.length || snapshot.nouns.length || snapshot.sections.length) {
    emit({ ...snapshot, ids: [], nouns: [], sections: [], order: [] });
  }
}

/** A spoken or typed yes acted, or an edit changed a card: the tray reloads its rows and today's brief
 *  now (``useLoadNamed``), so the new state shows (and its approve moment or change pulse plays) at once. */
export function cardsChanged() {
  emit({ ...snapshot, changed: snapshot.changed + 1 });
}

/** A reply this recent still means what it says after a page load: a phone tab reloads under you
 *  while the tray still shows "I'm pointing at…", so its cards light up again. Older ones never do. */
export const FRESH_REPLY_MINUTES = 30;

/** True when the reply was written within ``FRESH_REPLY_MINUTES`` of ``now``. */
export function freshReply(reply: { timestamp?: string | Date | null }, now: Date = new Date()): boolean {
  if (!reply.timestamp) return false;
  const age = differenceInMinutes(now, new Date(reply.timestamp));
  return age >= 0 && age < FRESH_REPLY_MINUTES;
}

/** A reloaded thread's latest reply: marks its cards once. ``seenOnly`` records it without marking:
 *  the thread's first read of an old reply (it never lights cards up on page load), and the reload
 *  after this surface's own send when its stream already marked them. */
export function referFromReply(
  reply: {
    id: string;
    referencedTodoIds?: string[] | null;
    referencedSections?: string[] | null;
    referencedNouns?: { type: string; id: string }[] | null;
    referencedOrder?: string[] | null;
  },
  seenOnly: boolean,
) {
  if (seenReplies.has(reply.id)) return;
  seenReplies.add(reply.id);
  // The cards already marked (a voice reply landing in history under its own id) stay put, never re-scroll.
  if (!seenOnly) {
    referTodos(
      reply.referencedTodoIds ?? [],
      reply.id,
      reply.referencedSections ?? [],
      nounRefs(reply.referencedNouns),
      reply.referencedOrder ?? undefined,
    );
  }
}

/** Forget the mark and the replies already seen (sign-out, tests). */
export function clearReferencedTodos() {
  lastReply = null;
  seenReplies.clear();
  cardNouns.clear();
  emit(EMPTY);
}

/** Listen for any change to the mark (``KoryPointingLayer`` reads it through this). */
export function subscribeReferenced(onChange: () => void) {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

/** The mark right now, read outside React. */
export function readReferenced(): ReferencedTodos {
  return snapshot;
}

export function useReferencedTodos(): ReferencedTodos {
  return useSyncExternalStore(subscribeReferenced, readReferenced);
}

/** A docked tray's ``call`` while a live call the person started is on (null otherwise): how often Kory
 *  has let go, so a tray that opened itself for its card folds back. The call's end lets go of the mark. */
export function useTrayCall(live: boolean): { released: number } | null {
  const { released } = useReferencedTodos();
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) unmarkReferencedTodos();
    wasLive.current = live;
  }, [live]);
  return useMemo(() => (live ? { released } : null), [live, released]);
}

/** A docked tray's ``reveal``: the first named card, scrolled into view when the tray rises.
 *  Held while the tray is open, so a reply then scrolls once (``useRevealReferenced``), not twice. */
export function useNamedReveal(open: boolean): string | null {
  const { ids } = useReferencedTodos();
  const held = useRef<string | null>(null);
  if (!open) held.current = ids.length ? `[data-todo-id="${CSS.escape(ids[0])}"]` : null;
  return held.current;
}

/** The cards and Overview sections Kory's latest reply names, for a docked tray's ``named``: on a
 *  phone the tray drops to its bar rather than cover one on the page, and during a call opens for a
 *  card only it shows. ``prefer`` is the sections: on the page, the part Kory is reading is the one the
 *  tray steps aside for. Null when the reply names none. */
export function useNamedCard(): { selector: string; nonce: number; prefer?: string } | null {
  const { ids, nouns, sections, named } = useReferencedTodos();
  if (!ids.length && !sections.length && !nouns.length) return null;
  const keys = sections.filter((entry) => !isTarget(entry)).map(sectionSelector);
  const parts = sections.filter((entry) => isTarget(entry) && !parseTarget(entry)).map(refSelector);
  const prefer = [...keys, ...parts, ...(sections.some((entry) => parseTarget(entry)) ? [POINTED] : [])].join(", ");
  // A record Kory names is wherever a surface carries its ref.
  const selectors = [
    ...ids.map((id) => `[data-todo-id="${CSS.escape(id)}"]`),
    ...nouns.map(refSelector),
    ...(prefer ? [prefer] : []),
  ];
  return { selector: selectors.join(", "), nonce: named, ...(prefer ? { prefer } : {}) };
}

/** Bring what Kory names into view, the least scroll that shows it, in its scrollers too. */
export function scrollToNamed(el: HTMLElement, reduce: boolean) {
  if (typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest", behavior: reduce ? "auto" : "smooth" });
}

// How long a named section's widget may take to load and still scroll into view (a slow scorecard
// can take seconds); one that never renders (a failed widget) stops being watched.
const SECTION_WAIT_MS = 30_000;

/** Scroll the first Overview section Kory names into view once per naming, and let go of a section you
 *  touch (you are acting on it). Mounted by the page that shows the sections; a section still loading
 *  when the reply lands gets the scroll as soon as it renders. */
export function useRevealSections(rootRef: RefObject<HTMLElement | null>) {
  const { sections, nonce } = useReferencedTodos();
  const reduce = usePrefersReducedMotion();
  const scrolled = useRef(0);
  const first = sections.find((entry) => !isTarget(entry));
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !first || scrolled.current === nonce) return;
    return watchFor(() => {
      const el = root.querySelector<HTMLElement>(sectionSelector(first));
      if (!el) return false;
      scrolled.current = nonce;
      scrollToNamed(el, reduce);
      return true;
    }, SECTION_WAIT_MS);
  }, [first, nonce, reduce, rootRef]);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const touch = (event: Event) => {
      const key = (event.target as Element | null)?.closest<HTMLElement>("[data-overview-section]")?.dataset.overviewSection;
      if (key) unmarkSections([key]);
    };
    root.addEventListener("pointerdown", touch, true);
    return () => root.removeEventListener("pointerdown", touch, true);
  }, [rootRef]);
}

const POINTED = "[data-kory-pointed]";
// A target on a page still loading (a table behind a fetch) gets marked when it renders, for a while.
const TARGET_WAIT_MS = 10_000;

/** Mark what Kory's latest reply names on any page by role and accessible name (``data-kory-pointed``),
 *  scroll the first into view once per naming, and let go of one you touch. A marked element the page
 *  re-renders (a refetched table) is marked again for as long as the reply names it, and a mark whose
 *  element stops fitting (a reused row showing another record) goes. Mounted once, in
 *  the app shell. Pointing never clicks: the element is only marked. */
export function usePointedTargets() {
  const { sections, nonce } = useReferencedTodos();
  const reduce = usePrefersReducedMotion();
  const scrolled = useRef(0);
  const key = sections.filter((entry) => parseTarget(entry)).join("\n");
  useEffect(() => {
    if (!key) return;
    const wanted = key.split("\n");
    const marked = new Map<string, HTMLElement>();
    const deadline = Date.now() + TARGET_WAIT_MS;
    // Watched until the reply lets go; one never found stops being looked for after the wait.
    const stop = watchFor(() => {
      const looking = Date.now() < deadline;
      for (const entry of wanted) {
        const target = parseTarget(entry)!;
        const el = marked.get(entry);
        // A mark the page changed under it (a reused row now showing another record, or hidden) goes.
        if (el && stillTarget(el, target)) continue;
        if (el) {
          el.removeAttribute("data-kory-pointed");
          marked.delete(entry);
        } else if (!looking) continue;
        const found = findTarget(target);
        if (!found) continue;
        found.setAttribute("data-kory-pointed", entry);
        marked.set(entry, found);
      }
      const first = wanted.map((entry) => marked.get(entry)).find(Boolean);
      if (first && scrolled.current !== nonce) {
        scrolled.current = nonce;
        scrollToNamed(first, reduce);
      }
      return false;
    }, null);
    const touch = (event: Event) => {
      const touched = [...marked].filter(([, el]) => el.contains(event.target as Node)).map(([entry]) => entry);
      if (touched.length) unmarkSections(touched);
    };
    document.addEventListener("pointerdown", touch, true);
    return () => {
      stop?.();
      document.removeEventListener("pointerdown", touch, true);
      for (const el of marked.values()) el.removeAttribute("data-kory-pointed");
    };
  }, [key, nonce, reduce]);
}

/** Scroll the first named card inside ``rootRef`` into view once per reply that names cards.
 *  Rows still loading when the reply lands get the scroll as soon as they render. */
export function useRevealReferenced(rootRef: RefObject<HTMLElement | null>, nonce: number, ids: readonly string[]) {
  const reduce = usePrefersReducedMotion();
  const scrolled = useRef(0);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !ids.length || scrolled.current === nonce) return;
    const rows = [...root.querySelectorAll<HTMLElement>("[data-todo-id]")];
    const row = ids.map((id) => rows.find((el) => el.dataset.todoId === id)).find(Boolean);
    if (!row) return;
    scrolled.current = nonce;
    scrollToNamed(row, reduce);
  });
}

/** A named card the tray hasn't loaded (one Kory just proposed) reloads the to-do queue and the
 *  deal tray's rows, once per naming, so it shows at once rather than at the next refetch; so does a
 *  card a yes or an edit just changed (``cardsChanged``). Mounted by the tray's host, not ``TodoTray``,
 *  so an empty tray (a first to-do) still loads it. */
export function useLoadNamed(rows: readonly { id: string }[], ready: boolean) {
  const { ids, nonce, changed } = useReferencedTodos();
  const queryClient = useQueryClient();
  // A skeleton (a card Kory is still writing) has no row to load yet.
  const missing = ready
    ? ids.filter((id) => !id.startsWith(PENDING_PREFIX) && !rows.some((row) => row.id === id)).join(" ")
    : "";
  const loaded = useRef("");
  const reloadedFor = useRef(changed);
  useEffect(() => {
    const key = `${nonce}:${missing}`;
    const stale = reloadedFor.current !== changed;
    if (!stale && (!missing || loaded.current === key)) return;
    loaded.current = key;
    reloadedFor.current = changed;
    void queryClient.invalidateQueries({ queryKey: ["agent-todos"] });
    invalidateDealTodoSurfaces(queryClient);
    // The brief's rows carry the card's wording too: a changed card reloads them with the queue.
    if (stale) void queryClient.invalidateQueries({ queryKey: COS_SESSION_QUERY_KEY });
  }, [changed, missing, nonce, queryClient]);
}
